import React from 'react';
import { AppBar, Toolbar, Button, Box, IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '../../../config/brand';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LogoutIcon from '@mui/icons-material/Logout';
import AppsIcon from '@mui/icons-material/Apps';
import { WmsPermissions } from './useWmsSession';

type ScreenKey = 'picking' | 'packing' | 'receiving' | 'cyclecount';

interface WmsNavBarProps {
  currentScreen: ScreenKey;
  cashierName: string;
  wmsPermissions: WmsPermissions;
  onLogout: () => void;
  extraLeftActions?: React.ReactNode;
}

const SCREEN_CONFIG: { key: ScreenKey; label: string; icon: React.ReactNode; route: string; permKey: keyof WmsPermissions; screenIdKey: keyof WmsPermissions }[] = [
  { key: 'picking', label: 'Toplama', icon: <PlaylistAddCheckIcon />, route: 'toplama', permKey: 'canPicking', screenIdKey: 'pickingScreenId' },
  { key: 'packing', label: 'Paketleme', icon: <Inventory2Icon />, route: 'paketleme', permKey: 'canPacking', screenIdKey: 'packingScreenId' },
  { key: 'receiving', label: 'Mal Kabul', icon: <MoveToInboxIcon />, route: 'malkabul', permKey: 'canReceiving', screenIdKey: 'receivingScreenId' },
  { key: 'cyclecount', label: 'Stok Sayım', icon: <FactCheckIcon />, route: 'stoksayim', permKey: 'canCycleCount', screenIdKey: 'cycleCountScreenId' },
];

const WmsNavBar: React.FC<WmsNavBarProps> = ({
  currentScreen, cashierName, wmsPermissions, onLogout, extraLeftActions,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  // sm breakpoint = 600px; altinda kompakt mobil duzene geciyoruz.
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const accessibleScreens = SCREEN_CONFIG.filter((cfg) => (
    Boolean(wmsPermissions[cfg.permKey]) && Boolean(wmsPermissions[cfg.screenIdKey])
  ));
  const shouldShowScreenChooser = accessibleScreens.length > 1;
  const shouldShowCenterLogo = accessibleScreens.length <= 1;

  const handleScreenChange = (cfg: typeof SCREEN_CONFIG[0]) => {
    const screenId = wmsPermissions[cfg.screenIdKey];
    if (screenId) {
      navigate(`/wms/${cfg.route}/${screenId}`);
    }
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          position: 'relative',
          minHeight: { xs: 52, sm: 56 },
          gap: { xs: 0.5, sm: 1 },
          px: { xs: 0.75, sm: 2 },
        }}
      >
        {shouldShowCenterLogo && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              component="img"
              src={BRAND.logos.wmsHeader}
              alt={`${BRAND.name} Logo`}
              sx={{
                height: { xs: 39, sm: 45 },
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: { xs: 0.5, sm: 0.75 },
            flex: 1,
            minWidth: 0,
            overflowX: 'auto',
            py: 0.25,
            '&::-webkit-scrollbar': { height: 4 },
          }}
        >
          {SCREEN_CONFIG.map(cfg => {
            if (!wmsPermissions[cfg.permKey]) return null;
            const isActive = cfg.key === currentScreen;

            // Mobilde sadece ikonu gosteriyoruz; etiketi accessible-label olarak title/aria'da tutuyoruz.
            if (isCompact) {
              return (
                <Tooltip key={cfg.key} title={cfg.label}>
                  <IconButton
                    size="small"
                    onClick={() => handleScreenChange(cfg)}
                    aria-label={`${cfg.label} ekranina git`}
                    sx={{
                      color: isActive ? 'primary.main' : 'text.secondary',
                      bgcolor: isActive ? 'rgba(21, 101, 192, 0.12)' : 'transparent',
                      border: '1px solid',
                      borderColor: isActive ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      flexShrink: 0,
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      minHeight: 40,
                      '&:hover': {
                        bgcolor: isActive ? 'rgba(21, 101, 192, 0.16)' : 'rgba(21, 101, 192, 0.06)',
                      },
                    }}
                  >
                    {cfg.icon}
                  </IconButton>
                </Tooltip>
              );
            }

            return (
              <Button
                key={cfg.key}
                size="medium"
                startIcon={cfg.icon}
                onClick={() => handleScreenChange(cfg)}
                aria-label={`${cfg.label} ekranına git`}
                sx={{
                  color: isActive ? 'primary.main' : 'text.secondary',
                  bgcolor: isActive ? 'rgba(21, 101, 192, 0.12)' : 'transparent',
                  border: '1px solid',
                  borderColor: isActive ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  px: 1.5,
                  fontSize: 14,
                  fontWeight: isActive ? 700 : 500,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: isActive ? 'rgba(21, 101, 192, 0.16)' : 'rgba(21, 101, 192, 0.06)',
                  },
                }}
              >
                {cfg.label}
              </Button>
            );
          })}
          {extraLeftActions}
        </Box>

        {shouldShowScreenChooser && (isCompact ? (
          <Tooltip title="Ekran Seçimi">
            <IconButton
              size="small"
              onClick={() => navigate('/wms/secim')}
              aria-label="Ekran seçimi"
              sx={{ flexShrink: 0, width: 40, height: 40, minWidth: 40, minHeight: 40 }}
            >
              <AppsIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            size="small"
            startIcon={<AppsIcon />}
            onClick={() => navigate('/wms/secim')}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Ekran Seçimi
          </Button>
        ))}

        {/* Personel adı çıkış ikonunun yanında, minimal metin olarak. */}
        {cashierName ? (
          <Box
            component="span"
            title={cashierName}
            sx={{
              maxWidth: { xs: 88, sm: 140 },
              flexShrink: 1,
              minWidth: 0,
              fontSize: { xs: 12, sm: 13 },
              fontWeight: 500,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'right',
              lineHeight: 1.2,
            }}
          >
            {cashierName}
          </Box>
        ) : null}
        <Tooltip title="Çıkış">
          <IconButton
            size="small"
            onClick={onLogout}
            aria-label="Çıkış yap"
            sx={{
              color: 'text.secondary',
              flexShrink: 0,
              width: { xs: 40, sm: 44 },
              height: { xs: 40, sm: 44 },
              minWidth: { xs: 40, sm: 44 },
              minHeight: { xs: 40, sm: 44 },
            }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default WmsNavBar;
